
import {
  deleteMemory,
  listMemories,
  type Memory,
  type MemoryRoom,
  type MemorySettings,
  type MemoryWing,
} from "../api/settings.js";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

type MemoryView = {
  id: string;
  title: string;
  path: string;
  summary: string;
  content: string;
  source: string;
  keywords: string;
  created: string;
  rawSearch: string;
};

type RoomView = {
  id: string;
  wing: string;
  name: string;
  description: string;
  memories: number;
};

type WingView = {
  id: string;
  name: string;
  memories: number;
  rooms: RoomView[];
};

function formatDate(seconds: number): string {
  if (!seconds) return "Unknown date";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(seconds * 1000));
}

function memoryView(memory: Memory): MemoryView {
  const title = memory.title || memory.summary || "Untitled memory";
  const summary = memory.summary || memory.content.slice(0, 180);
  const source = memory.source || "Agent memory";
  return {
    id: memory.id,
    title: escapeHtml(title),
    path: escapeHtml(`${memory.wing} / ${memory.room}`),
    summary: escapeHtml(summary),
    content: escapeHtml(memory.content),
    source: escapeHtml(source),
    keywords: escapeHtml(memory.keywords),
    created: escapeHtml(formatDate(memory.created_at)),
    rawSearch: `${title} ${summary} ${memory.content} ${memory.wing} ${memory.room} ${memory.keywords}`.toLowerCase(),
  };
}

function roomView(room: MemoryRoom): RoomView {
  return {
    id: room.id,
    wing: escapeHtml(room.wing),
    name: escapeHtml(room.name),
    description: escapeHtml(room.description),
    memories: room.memories,
  };
}

function wingView(wing: MemoryWing, rooms: MemoryRoom[]): WingView {
  return {
    id: wing.id,
    name: escapeHtml(wing.name),
    memories: wing.memories,
    rooms: rooms.filter((room) => room.wing === wing.name).map(roomView),
  };
}

const styles = css`
  .memory-overview {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .memory-stat {
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 12px;
  }

  .memory-stat .value {
    color: var(--foreground);
    font-size: 24px;
    font-weight: 650;
    line-height: 1;
  }

  .memory-stat .label {
    color: var(--muted-foreground);
    font-size: 11px;
    letter-spacing: 0.08em;
    margin-top: 7px;
    text-transform: uppercase;
  }

  .memory-workspace {
    align-items: start;
    display: grid;
    gap: 16px;
    grid-template-columns: minmax(220px, 0.8fr) minmax(0, 1.2fr);
    margin-top: 20px;
  }

  .panels {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
  }

  .memory-map,
  .memory-ledger,
  .memory-detail {
    border: 1px solid var(--border);
    border-radius: 8px;
    min-width: 0;
  }

  .memory-map {
    background:
      linear-gradient(90deg, color-mix(in srgb, var(--border) 45%, transparent) 1px, transparent 1px) 0 0 / 24px 24px,
      linear-gradient(color-mix(in srgb, var(--border) 45%, transparent) 1px, transparent 1px) 0 0 / 24px 24px;
    padding: 14px;
  }

  .map-wing {
    display: grid;
    gap: 8px;
  }

  .map-wing + .map-wing {
    border-top: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
    margin-top: 14px;
    padding-top: 14px;
  }

  .map-wing-head {
    align-items: baseline;
    display: flex;
    gap: 10px;
    justify-content: space-between;
  }

  .map-wing-name {
    color: var(--foreground);
    font-size: 13px;
    font-weight: 650;
    overflow-wrap: anywhere;
  }

  .map-wing-count {
    color: var(--muted-foreground);
    flex: 0 0 auto;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }

  .map-room {
    align-items: center;
    color: var(--muted-foreground);
    display: grid;
    font-size: 12px;
    gap: 8px;
    grid-template-columns: 18px minmax(0, 1fr) auto;
    min-height: 24px;
  }

  .map-room::before {
    background: var(--background);
    border: 1px solid var(--border);
    border-radius: 999px;
    content: "";
    height: 7px;
    justify-self: center;
    width: 7px;
  }

  .room-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .room-count {
    color: var(--foreground);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }

  .status-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .memory-tools {
    display: grid;
    gap: 12px;
    margin-bottom: 14px;
  }

  .memory-search {
    position: relative;
  }

  .memory-search input {
    padding-left: 34px;
  }

  .memory-search::before {
    color: var(--muted-foreground);
    content: "⌕";
    font-size: 19px;
    left: 12px;
    line-height: 1;
    position: absolute;
    top: 8px;
  }

  input {
    background: var(--background);
    border: 1px solid var(--input);
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--foreground);
    font: inherit;
    font-size: 14px;
    height: 36px;
    outline: none;
    padding: 8px 10px;
    width: 100%;
  }

  input:focus {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px var(--ring-shadow);
  }

  .memory-ledger {
    overflow: hidden;
  }

  .memory-row {
    background: transparent;
    border: 0;
    border-bottom: 1px solid var(--border);
    color: inherit;
    cursor: pointer;
    display: grid;
    gap: 5px;
    padding: 12px 14px;
    text-align: left;
    width: 100%;
  }

  .memory-row:last-child {
    border-bottom: 0;
  }

  .memory-row:hover,
  .memory-row[aria-current="true"] {
    background: var(--accent);
  }

  .memory-row-title {
    color: var(--foreground);
    font-size: 14px;
    font-weight: 650;
    overflow-wrap: anywhere;
  }

  .memory-row-path,
  .memory-row-summary,
  .memory-detail-meta,
  .memory-tags,
  .muted,
  .hint {
    color: var(--muted-foreground);
    font-size: 12px;
    line-height: 1.45;
  }

  .memory-row-summary {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .memory-detail {
    display: grid;
    gap: 14px;
    padding: 14px;
  }

  .memory-detail-head {
    align-items: start;
    display: flex;
    gap: 14px;
    justify-content: space-between;
  }

  h3 {
    color: var(--foreground);
    font-size: 16px;
    line-height: 1.25;
    margin: 0;
    overflow-wrap: anywhere;
  }

  .memory-detail-content {
    color: var(--foreground);
    font-size: 13px;
    line-height: 1.55;
    max-height: 280px;
    overflow: auto;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
    margin: 8px 0 0;
  }

  .error:empty {
    display: none;
  }

  @media (max-width: 767px) {
    .memory-overview,
    .memory-workspace {
      grid-template-columns: 1fr;
    }
  }
`;

export class AppSettingsMemory extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsMemory)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  #wings;
  #rooms;
  #memories;
  #loaded;
  #error;
  #query;
  #selectedId;
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #textStart8;
  #textEnd8;
  #textLast8;
  #attr0;
  #attrLast0;
  #eventContexts = {};
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  #setWings(value) {
    if (Object.is(this.#wings, value)) return;
    this.#wings = value;
    this.#updateBindings('wings');
  }

  #setRooms(value) {
    if (Object.is(this.#rooms, value)) return;
    this.#rooms = value;
    this.#updateBindings('rooms');
  }

  #setMemories(value) {
    if (Object.is(this.#memories, value)) return;
    this.#memories = value;
    this.#updateBindings('memories');
  }

  #setLoaded(value) {
    if (Object.is(this.#loaded, value)) return;
    this.#loaded = value;
    this.#updateBindings('loaded');
  }

  #setError(value) {
    if (Object.is(this.#error, value)) return;
    this.#error = value;
    this.#updateBindings('error');
  }

  #setQuery(value) {
    if (Object.is(this.#query, value)) return;
    this.#query = value;
    this.#updateBindings('query');
  }

  #setSelectedId(value) {
    if (Object.is(this.#selectedId, value)) return;
    this.#selectedId = value;
    this.#updateBindings('selectedId');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void listMemories()
        .then((settings: MemorySettings) => {
        this.#setWings(settings.wings);
        this.#setRooms(settings.rooms);
        this.#setMemories(settings.memories);
        this.#setLoaded(true);
        this.#setError("");
        if (this.#selectedId && !settings.memories.some((memory) => memory.id === this.#selectedId)) {
            this.#setSelectedId(settings.memories[0]?.id ?? "");
        }
    })
        .catch(() => {
        this.#setError("Failed to load memories.");
    });
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }


  #initState() {
    this.#wings = [];
    this.#rooms = [];
    this.#memories = [];
    this.#loaded = false;
    this.#error = "";
    this.#query = "";
    this.#selectedId = "";
  }

  #render() {
    const memoryViews = this.#memories.map(memoryView);
    const trimmed = this.#query.trim().toLowerCase();
    const filteredMemories = trimmed
    ? memoryViews.filter((memory) => memory.rawSearch.includes(trimmed))
    : memoryViews;
    const selectedMemory = filteredMemories.find((memory) => memory.id === this.#selectedId)
    ?? filteredMemories[0]
    ?? null;
    const memoryWingViews = this.#wings.map((wing) => wingView(wing, this.#rooms));
    return `<style>${this.#escText(styles)}</style><app-settings-section title="Memory palace" description="Review durable memories your agents can recall across threads. New memories are still created by asking the agent to remember something."><div class="memory-overview"><div class="memory-stat"><div class="value"><!--argon-text:0-->${this.#wings.length}<!--/argon-text:0--></div><div class="label">Wings</div></div><div class="memory-stat"><div class="value"><!--argon-text:1-->${this.#rooms.length}<!--/argon-text:1--></div><div class="label">Rooms</div></div><div class="memory-stat"><div class="value"><!--argon-text:2-->${this.#memories.length}<!--/argon-text:2--></div><div class="label">Memories</div></div></div><div class="status-row"><app-button variant="outline" size="sm" data-argon="9230">Refresh</app-button><span class="muted"><!--argon-text:3-->${this.#escText(this.#loaded ? "Showing saved memory structure." : "Loading memories...")}<!--/argon-text:3--></span></div><p class="error"><!--argon-text:4-->${this.#escText(this.#error)}<!--/argon-text:4--></p></app-settings-section><div class="memory-workspace"><app-settings-section title="Palace map" description="Wings hold rooms; rooms hold individual memories. Empty rooms stay visible so the structure is easy to audit."><!--argon-text:5-->${(this.#wings.length > 0) ? `<div class="memory-map">${memoryWingViews.map((wing) => `<div class="map-wing"><div class="map-wing-head"><div class="map-wing-name">${wing.name}</div><div class="map-wing-count">${wing.memories} memories</div></div>${(wing.rooms.length > 0) ? wing.rooms.map((room) => `<div class="map-room" title="${room.description}"><span class="room-name">${room.name}</span><span class="room-count">${room.memories}</span></div>`).join("") : "<p class=\"muted\">No rooms yet.</p>"}</div>`).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No memory wings yet." : "Loading palace map...")}</p>`}<!--/argon-text:5--></app-settings-section><div class="panels"><app-settings-section title="Memory ledger" description="Search titles, summaries, rooms, and original contents. Removing a memory deletes the saved drawer and its search card."><div class="memory-tools"><label class="memory-search"><input name="memory-query" value="${this.#escAttr(this.#query)}" placeholder="Search memories" aria-label="Search memories" autocomplete="off" data-argon="11931" data-argon-bind="0"></label><span class="hint"><!--argon-text:6-->${filteredMemories.length} of ${this.#memories.length} memories shown<!--/argon-text:6--></span></div><!--argon-text:7-->${(filteredMemories.length > 0) ? `<div class="memory-ledger">${filteredMemories.map((memory, __argonIndex) => this.#mapEventItem(`<button type="button" class="memory-row" aria-current="${this.#escAttr((selectedMemory?.id === memory.id) ? "true" : "false")}" data-argon="12587"><span class="memory-row-title">${memory.title}</span><span class="memory-row-path">${memory.path}</span><span class="memory-row-summary">${memory.summary}</span></button>`, "initial:10:12587:" + __argonIndex, { 12587: () => { this.#setSelectedId(memory.id); } }, ["12587"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No memories match this search." : "Loading memories...")}</p>`}<!--/argon-text:7--></app-settings-section><app-settings-section title="Selected memory" description="Inspect the stored summary, original content, source, and search keywords before removing anything."><!--argon-text:8-->${selectedMemory ? `<div class="memory-detail"><div class="memory-detail-head"><div><h3>${selectedMemory.title}</h3><div class="memory-detail-meta">${selectedMemory.path} - ${selectedMemory.created}</div></div><app-button variant="outline" size="sm" data-argon="13775">Remove</app-button></div><p class="muted">${selectedMemory.summary}</p><div class="memory-detail-content">${selectedMemory.content}</div><div class="memory-tags">Source: ${selectedMemory.source}</div>${selectedMemory.keywords ? `<div class="memory-tags">Keywords: ${selectedMemory.keywords}</div>` : this.#escText("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "Select a memory to inspect it." : "Loading selected memory...")}</p>`}<!--/argon-text:8--></app-settings-section></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
        case 'argon-text:8':
          this.#textStart8 = marker;
          break;
        case '/argon-text:8':
          this.#textEnd8 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const memoryViews = this.#memories.map(memoryView);
    const trimmed = this.#query.trim().toLowerCase();
    const filteredMemories = trimmed
    ? memoryViews.filter((memory) => memory.rawSearch.includes(trimmed))
    : memoryViews;
    const selectedMemory = filteredMemories.find((memory) => memory.id === this.#selectedId)
    ?? filteredMemories[0]
    ?? null;
    const memoryWingViews = this.#wings.map((wing) => wingView(wing, this.#rooms));
    if (prop === undefined || prop === 'wings') {
      const __argonNext = `${this.#wings.length}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'rooms') {
      const __argonNext = `${this.#rooms.length}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'memories') {
      const __argonNext = `${this.#memories.length}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'loaded') {
      const __argonNext = `${this.#escText(this.#loaded ? "Showing saved memory structure." : "Loading memories...")}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${this.#escText(this.#error)}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined || prop === 'wings' || prop === 'rooms' || prop === 'loaded') {
      const __argonNext = `${(this.#wings.length > 0) ? `<div class="memory-map">${memoryWingViews.map((wing) => `<div class="map-wing"><div class="map-wing-head"><div class="map-wing-name">${wing.name}</div><div class="map-wing-count">${wing.memories} memories</div></div>${(wing.rooms.length > 0) ? wing.rooms.map((room) => `<div class="map-room" title="${room.description}"><span class="room-name">${room.name}</span><span class="room-count">${room.memories}</span></div>`).join("") : "<p class=\"muted\">No rooms yet.</p>"}</div>`).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No memory wings yet." : "Loading palace map...")}</p>`}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined || prop === 'query' || prop === 'memories') {
      const __argonNext = `${filteredMemories.length} of ${this.#memories.length} memories shown`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined || prop === 'query' || prop === 'memories' || prop === 'selectedId' || prop === 'loaded') {
      const __argonNext = `${(filteredMemories.length > 0) ? `<div class="memory-ledger">${filteredMemories.map((memory, __argonIndex) => this.#mapEventItem(`<button type="button" class="memory-row" aria-current="${this.#escAttr((selectedMemory?.id === memory.id) ? "true" : "false")}" data-argon="12587"><span class="memory-row-title">${memory.title}</span><span class="memory-row-path">${memory.path}</span><span class="memory-row-summary">${memory.summary}</span></button>`, "7:10:12587:" + __argonIndex, { 12587: () => { this.#setSelectedId(memory.id); } }, ["12587"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No memories match this search." : "Loading memories...")}</p>`}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if (prop === undefined || prop === 'query' || prop === 'memories' || prop === 'selectedId' || prop === 'loaded') {
      const __argonNext = `${selectedMemory ? `<div class="memory-detail"><div class="memory-detail-head"><div><h3>${selectedMemory.title}</h3><div class="memory-detail-meta">${selectedMemory.path} - ${selectedMemory.created}</div></div><app-button variant="outline" size="sm" data-argon="13775">Remove</app-button></div><p class="muted">${selectedMemory.summary}</p><div class="memory-detail-content">${selectedMemory.content}</div><div class="memory-tags">Source: ${selectedMemory.source}</div>${selectedMemory.keywords ? `<div class="memory-tags">Keywords: ${selectedMemory.keywords}</div>` : this.#escText("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "Select a memory to inspect it." : "Loading selected memory...")}</p>`}`;
      if (__argonNext !== this.#textLast8) {
        this.#textLast8 = __argonNext;
        this.#replaceRange(this.#textStart8, this.#textEnd8, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'query') && this.#attr0) {
      const __argonNext = `${this.#query}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('value', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '9230').forEach(el => this.#listen(el, '9230', 'click', () => {
    void listMemories()
        .then((settings: MemorySettings) => {
        this.#setWings(settings.wings);
        this.#setRooms(settings.rooms);
        this.#setMemories(settings.memories);
        this.#setLoaded(true);
        this.#setError("");
        if (this.#selectedId && !settings.memories.some((memory) => memory.id === this.#selectedId)) {
            this.#setSelectedId(settings.memories[0]?.id ?? "");
        }
    })
        .catch(() => {
        this.#setError("Failed to load memories.");
    });
}));
    this.#eventTargets(scope, '11931').forEach(el => this.#listen(el, '11931', 'input', (event: Event) => {
    this.#setQuery((event.target as HTMLInputElement).value);
}));
    this.#eventTargets(scope, '12587').forEach(el => this.#listen(el, '12587', 'click', () => { this.#setSelectedId(memory.id); }));
    this.#eventTargets(scope, '13775').forEach(el => this.#listen(el, '13775', 'click', () => {
    if (!window.confirm("Remove this memory? This cannot be undone."))
        return;
    void deleteMemory(selectedMemory.id)
        .then(() => listMemories())
        .then((settings: MemorySettings) => {
        this.#setWings(settings.wings);
        this.#setRooms(settings.rooms);
        this.#setMemories(settings.memories);
        this.#setLoaded(true);
        this.#setError("");
        if (this.#selectedId && !settings.memories.some((memory) => memory.id === this.#selectedId)) {
            this.#setSelectedId(settings.memories[0]?.id ?? "");
        }
    })
        .catch(() => {
        this.#setError("Failed to remove memory.");
    });
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
